import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { FlowEngine } from '../../services/flows';
import type { Step } from '../../services/flows/types';
import { editImage } from '../../services/imageEdit';
import { logger } from '../../utils/logger';

// Create component logger for ImageEditStep
const imageEditStepLogger = logger.withComponent('ImageEditStep');

interface ImageEditStepType extends Step {
  type: 'imageEdit';
  prompt: string;
  imageVar: string; // Variable name containing source image
  storeAs?: string; // Variable name to store result
  loadingMessage?: string;
  timeout?: number;
  retries?: number;
  next?: string;
  errorNext?: string;
}

interface ImageEditStepProps {
  step: ImageEditStepType;
  engine: FlowEngine;
  onMessage: (message: { from: string; text: string; temporary?: boolean; isLoading?: boolean }) => void;
}

const ImageEditStep: React.FC<ImageEditStepProps> = ({ step, engine, onMessage }) => {
  const [, setIsLoading] = useState(true);
  const hasExecutedRef = useRef(false);

  // Utility function to convert base64 to File
  const base64ToFile = (base64: string, filename: string): File => {
    // Remove data URI prefix if present
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Determine MIME type from data URI or default to PNG
    let mimeType = 'image/png';
    if (base64.startsWith('data:image/')) {
      const match = base64.match(/data:image\/([^;]+)/);
      if (match) {
        mimeType = `image/${match[1]}`;
      }
    }

    return new File([byteArray], filename, { type: mimeType });
  };

  const executeImageEdit = useCallback(async () => {
    // Show loading indicator with a unique temporary message
    // The loading message will be automatically removed when the next message is added
    const loadingText = step.loadingMessage ? __(step.loadingMessage) : __('Analyzing image...');
    onMessage({ from: 'bot', text: loadingText, temporary: true, isLoading: true });

    try {
      // Get current flow variables
      const flowState = engine.getState();
      const variables = flowState.variables;

      // Retrieve image from variable
      const imageData = variables[step.imageVar];
      if (!imageData) {
        // Missing image - send error message and navigate to errorNext
        setIsLoading(false);
        onMessage({ from: 'bot', text: __('No image found to analyze. Please provide an image first.') });
        engine.handleStepError();
        return;
      }

      // Convert base64 image to File object
      const imageFile = base64ToFile(imageData, 'input_image.png');

      // Execute image edit
      const result = await editImage(
        imageFile,
        step.prompt || __('Edit this image'), 
        {
          timeout: step.timeout,
          retries: step.retries,
        }
      );

      setIsLoading(false);

      if (step.storeAs && result.data && result.data[0]) {
        // Store result in variable if specified
        engine.setVariable(step.storeAs, result.data[0].base64);
      }

      // Navigate to success next step
      if (step.next) {
        engine.goToNext(step.next);
      }
    } catch (error) {
      setIsLoading(false);
      imageEditStepLogger.error('Image edit failed', error);
      onMessage({ from: 'bot', text: __('Image analysis failed. Please try again.') });
      engine.handleStepError();
    }
  }, [engine, step, onMessage, setIsLoading]);

  // Execute image edit call on mount
  useEffect(() => {
    if (!hasExecutedRef.current) {
      hasExecutedRef.current = true;
      executeImageEdit();
    }
  }, [executeImageEdit]);

  // Component is purely orchestration - no visual elements
  return null;
};

export default ImageEditStep;